/**
 * Construcción de ítems jugables a partir del JSON y verificación de respuestas.
 *
 * Capa intermedia pura entre el contenido declarativo y la vista: la vista sólo
 * pinta `RuntimeItem` y devuelve `ItemResponse`, sin decidir nunca qué es
 * correcto. Así todos los arquetipos se prueban sin navegador.
 *
 * Las piezas se barajan con una semilla derivada del propio minijuego, de modo
 * que el orden de presentación es estable entre partidas pero nunca coincide
 * con la solución: el contenido no puede regalar la respuesta por accidente.
 */

import type { MinigamePiece, MinigameSpec } from '../types/game';
import {
  checkBuilding,
  checkChoice,
  checkMatching,
  checkNumeric,
  checkOrdering,
  checkSorting,
  checkText,
  checkWordsearch,
  type CheckResult
} from './check';
import { createRng, formatNumber, generateItems, type GeneratedItem } from './items';

/** Modelo del widget que pinta la vista, ya sin referencia al JSON original. */
export type WidgetModel =
  | {
      kind: 'choice';
      opciones: string[];
      correcta: number;
      misconception: string | null;
      explicacion: string;
    }
  | { kind: 'numeric'; respuesta: number; explicacion: string }
  | { kind: 'text'; blancos: string[][]; explicacion: string }
  | {
      kind: 'ordering';
      /** Piezas en orden de presentación (barajado). */
      piezas: { id: string; label: string }[];
      /** Secuencia correcta de ids. */
      orden: string[];
      rango?: [number, number];
      explicacion: string;
    }
  | {
      kind: 'matching';
      /** Pares en orden canónico: el índice es la identidad de la pareja. */
      pares: { izquierda: string; derecha: string }[];
      /** Índices de `pares` en el orden de presentación de la columna derecha. */
      ordenDerecha: number[];
      explicacion: string;
    }
  | {
      kind: 'sorting';
      piezas: { id: string; label: string; bucket: string }[];
      cestas: { id: string; label: string }[];
      explicacion: string;
    }
  | {
      kind: 'building';
      piezas: { id: string; label: string; value: number }[];
      objetivo: number;
      solucion: string[];
      explicacion: string;
    }
  | {
      kind: 'wordsearch';
      /** Rejilla de letras tal cual la publicó el OA, una fila por elemento. */
      rejilla: string[];
      /** Palabras a encontrar, ya comprobado que están en la rejilla. */
      palabras: string[];
      explicacion: string;
    };

export interface RuntimeItem {
  pregunta: string;
  expresion?: string;
  widget: WidgetModel;
}

/** Respuesta del jugador, tipada por arquetipo. */
export type ItemResponse =
  | { kind: 'choice'; index: number }
  | { kind: 'numeric'; value: number }
  | { kind: 'text'; values: string[] }
  | { kind: 'ordering'; ids: string[] }
  | { kind: 'matching'; pairs: Record<number, number> }
  | { kind: 'sorting'; buckets: Record<string, string> }
  | { kind: 'building'; ids: string[] }
  | { kind: 'wordsearch'; words: string[] };

/** Semilla estable derivada de un texto (FNV-1a). */
export function hashSeed(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function stableShuffle<T>(seed: number, items: T[]): T[] {
  const rng = createRng(seed);
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Baraja piezas para presentarlas. Si el azar las dejara justo en el orden
 * correcto, se rota una posición para no regalar la respuesta.
 */
function displayPieces(seedText: string, piezas: MinigamePiece[], orden?: string[]): MinigamePiece[] {
  const shuffled = stableShuffle(hashSeed(seedText), piezas);
  if (!orden || orden.length !== shuffled.length) return shuffled;
  const leaked = shuffled.every((piece, index) => piece.id === orden[index]);
  if (!leaked) return shuffled;
  return [...shuffled.slice(1), shuffled[0]];
}

function widgetFromGenerated(item: GeneratedItem, asChoice: boolean): WidgetModel {
  if (asChoice) {
    return {
      kind: 'choice',
      opciones: item.opciones,
      correcta: item.correcta,
      misconception: item.misconception,
      explicacion: item.explicacion
    };
  }
  return {
    kind: 'numeric',
    respuesta: typeof item.respuesta === 'number' ? item.respuesta : Number(item.respuesta),
    explicacion: item.explicacion
  };
}

function authoredChoice(opciones: string[], correcta: number, explicacion?: string): WidgetModel {
  return {
    kind: 'choice',
    opciones,
    correcta,
    misconception: null,
    explicacion: explicacion ?? `La respuesta es ${opciones[correcta] ?? ''}.`
  };
}

/** Expande el spec en la lista de ítems que se jugarán, en orden. */
export function buildItems(spec: MinigameSpec): RuntimeItem[] {
  switch (spec.kind) {
    case 'opcion-multiple': {
      if (spec.generador) {
        return generateItems(spec.generador, spec.cantidad ?? 1, spec.semilla ?? 1).map((item) => ({
          pregunta: item.pregunta,
          expresion: item.expresion,
          widget: widgetFromGenerated(item, true)
        }));
      }
      return [{
        pregunta: spec.enunciado,
        widget: authoredChoice(spec.opciones ?? [], spec.correcta ?? 0, spec.explicacion)
      }];
    }
    case 'respuesta-numerica': {
      if (spec.generador) {
        return generateItems(spec.generador, spec.cantidad ?? 1, spec.semilla ?? 1).map((item) => ({
          pregunta: item.pregunta,
          expresion: item.expresion,
          widget: widgetFromGenerated(item, false)
        }));
      }
      return [{
        pregunta: spec.enunciado,
        expresion: spec.expresion,
        widget: { kind: 'numeric', respuesta: spec.respuesta ?? 0, explicacion: spec.explicacion ?? '' }
      }];
    }
    case 'respuesta-texto': {
      return [{
        pregunta: spec.enunciado,
        widget: {
          kind: 'text',
          blancos: spec.blancos,
          explicacion: spec.explicacion
            ?? spec.blancos.map((b) => b[0]).filter((v) => v !== undefined).join(', ')
        }
      }];
    }
    case 'ordenar':
    case 'recta-numerica': {
      const orden = spec.orden
        .map((id) => spec.piezas.find((p) => p.id === id)?.label ?? id)
        .join(' → ');
      return [{
        pregunta: spec.enunciado,
        widget: {
          kind: 'ordering',
          piezas: displayPieces(spec.id, spec.piezas, spec.orden).map((p) => ({ id: p.id, label: p.label })),
          orden: spec.orden,
          rango: spec.rango,
          explicacion: spec.explicacion ?? `El orden correcto es ${orden}.`
        }
      }];
    }
    case 'emparejar': {
      const indices = spec.pares.map((_, index) => index);
      const ordenDerecha = stableShuffle(hashSeed(`${spec.id}:derecha`), indices);
      return [{
        pregunta: spec.enunciado,
        widget: {
          kind: 'matching',
          pares: spec.pares.map((p) => ({ izquierda: p.izquierda, derecha: p.derecha })),
          ordenDerecha: ordenDerecha.every((v, i) => v === i) ? [...indices.slice(1), indices[0]] : ordenDerecha,
          explicacion: spec.explicacion ?? spec.pares.map((p) => `${p.izquierda} ↔ ${p.derecha}`).join(', ')
        }
      }];
    }
    case 'clasificar': {
      return [{
        pregunta: spec.enunciado,
        widget: {
          kind: 'sorting',
          piezas: displayPieces(spec.id, spec.piezas).map((p) => ({
            id: p.id,
            label: p.label,
            bucket: p.bucket ?? ''
          })),
          cestas: spec.cestas.map((c) => ({ id: c.id, label: c.label })),
          explicacion: spec.explicacion ?? spec.piezas
            .map((p) => `${p.label} → ${spec.cestas.find((c) => c.id === p.bucket)?.label ?? p.bucket}`)
            .join(', ')
        }
      }];
    }
    case 'construir': {
      const partes = spec.solucion
        .map((id) => spec.piezas.find((p) => p.id === id))
        .filter((p) => p !== undefined)
        .map((p) => `${p!.label} (${formatNumber(p!.value ?? 0)})`)
        .join(' + ');
      return [{
        pregunta: spec.enunciado,
        widget: {
          kind: 'building',
          piezas: displayPieces(spec.id, spec.piezas).map((p) => ({
            id: p.id,
            label: p.label,
            value: p.value ?? 0
          })),
          objetivo: spec.objetivo,
          solucion: spec.solucion,
          explicacion: spec.explicacion ?? `${partes} = ${formatNumber(spec.objetivo)}.`
        }
      }];
    }
    case 'sopa-letras': {
      return [{
        pregunta: spec.enunciado,
        widget: {
          kind: 'wordsearch',
          rejilla: spec.rejilla,
          palabras: spec.palabras,
          explicacion: spec.explicacion ?? `Las palabras eran: ${spec.palabras.join(', ')}.`
        }
      }];
    }
    case 'rafaga': {
      // Una ráfaga mide agilidad: se responde con un toque. Escribir la cifra
      // ya es el trabajo de `respuesta-numerica`, y aquí sólo frenaría la racha.
      return generateItems(spec.generador, spec.cantidad, spec.semilla ?? 1).map((item) => ({
        pregunta: item.pregunta,
        expresion: item.expresion,
        widget: widgetFromGenerated(item, true)
      }));
    }
  }
}

/** Verifica la respuesta del jugador contra el ítem. */
export function checkResponse(item: RuntimeItem, response: ItemResponse): CheckResult {
  const widget = item.widget;
  if (response.kind === 'choice') {
    return widget.kind === 'choice' ? checkChoice(widget.correcta, response.index) : { correct: false };
  }
  if (response.kind === 'numeric') {
    return widget.kind === 'numeric' ? checkNumeric(widget.respuesta, response.value) : { correct: false };
  }
  if (response.kind === 'text') {
    return widget.kind === 'text' ? checkText(widget.blancos, response.values) : { correct: false };
  }
  if (response.kind === 'ordering') {
    return widget.kind === 'ordering' ? checkOrdering(widget.piezas, widget.orden, response.ids) : { correct: false };
  }
  if (response.kind === 'matching') {
    return widget.kind === 'matching' ? checkMatching(widget.pares, response.pairs) : { correct: false };
  }
  if (response.kind === 'sorting') {
    return widget.kind === 'sorting'
      ? checkSorting(widget.piezas, widget.cestas, response.buckets)
      : { correct: false };
  }
  if (response.kind === 'wordsearch') {
    return widget.kind === 'wordsearch'
      ? checkWordsearch(widget.palabras, response.words)
      : { correct: false };
  }
  return widget.kind === 'building'
    ? checkBuilding(widget.piezas, widget.objetivo, widget.solucion, response.ids)
    : { correct: false };
}

/** Texto de la solución, para revelarla cuando se agotan los intentos. */
export function solutionText(item: RuntimeItem): string {
  const widget = item.widget;
  switch (widget.kind) {
    case 'choice':
      return widget.opciones[widget.correcta] ?? '';
    case 'numeric':
      return formatNumber(widget.respuesta);
    case 'text':
      return widget.blancos.map((b) => b[0] ?? '').join(' · ');
    case 'ordering':
      return widget.orden
        .map((id) => widget.piezas.find((p) => p.id === id)?.label ?? id)
        .join(' → ');
    case 'matching':
      return widget.pares.map((p) => `${p.izquierda} ↔ ${p.derecha}`).join(' · ');
    case 'sorting':
      return widget.piezas
        .map((p) => `${p.label} → ${widget.cestas.find((c) => c.id === p.bucket)?.label ?? p.bucket}`)
        .join(' · ');
    case 'building':
      return widget.piezas
        .filter((p) => widget.solucion.includes(p.id))
        .map((p) => p.label)
        .join(' + ');
    case 'wordsearch':
      return widget.palabras.join(' · ');
  }
}

/** Retroalimentación del error: detalle concreto primero, misconcepción después. */
export function failureText(item: RuntimeItem, result: CheckResult): string {
  const parts: string[] = [];
  if (result.detalle) parts.push(result.detalle);
  if (item.widget.kind === 'choice' && item.widget.misconception) parts.push(item.widget.misconception);
  return parts.join(' ');
}
